import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

/**
 * Phase 11 — email notifications on submission. Two independent halves:
 * an internal alert to a fixed list of team recipients, and an optional
 * autoresponder back to the submitter (mapped to one of the form's own
 * email-type fields, since the submitter's address isn't known until the
 * values come in). Mirrors schema.settings.notifications, read by
 * FormNotificationService server-side; sending itself is fire-and-forget
 * there so a bad address here never affects the visitor's submission.
 */
export default function NotificationsPanel({ notifications, fields, onChange, onClose }) {
  const current = notifications || { internal: { enabled: false, recipients: [] }, confirmation: { enabled: false } };
  const [newRecipient, setNewRecipient] = useState('');
  const set = (patch) => onChange({ ...current, ...patch });
  const setInternal = (patch) => set({ internal: { ...current.internal, ...patch } });
  const setConfirmation = (patch) => set({ confirmation: { ...current.confirmation, ...patch } });

  const emailFields = fields.filter((f) => f.type === 'email');
  const recipients = current.internal?.recipients || [];

  const addRecipient = () => {
    const email = newRecipient.trim();
    if (!email || recipients.includes(email)) return;
    setInternal({ recipients: [...recipients, email] });
    setNewRecipient('');
  };

  const removeRecipient = (email) => setInternal({ recipients: recipients.filter((r) => r !== email) });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[480px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Notifications</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="flex items-center gap-2 text-[13px] font-medium text-[#181818] cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={!!current.internal?.enabled}
                onChange={(e) => setInternal({ enabled: e.target.checked })}
                className="accent-[#0176D3] w-4 h-4"
              />
              Email my team on every submission
            </label>

            {current.internal?.enabled && (
              <div className="space-y-2">
                <label className={labelClass}>Recipients</label>
                {recipients.map((email) => (
                  <div key={email} className="flex items-center gap-2">
                    <span className="flex-1 px-3 py-1.5 border border-[#D8DDE6] rounded-[4px] text-[13px] text-[#181818] bg-[#F4F6F9]">{email}</span>
                    <button onClick={() => removeRecipient(email)} className="p-1.5 text-[#706E6B] hover:text-[#EA001E]">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input
                    className={inputClass}
                    type="email"
                    placeholder="team@company.com"
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecipient())}
                  />
                  <button onClick={addRecipient} className="p-2 text-[#0176D3] hover:bg-[#0176D3]/10 rounded-[4px] shrink-0">
                    <Plus size={16} />
                  </button>
                </div>
                {!recipients.length && (
                  <p className="text-[11px] text-[#EA001E]">Add at least one recipient — notifications are off until then.</p>
                )}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-[#F0F1F3]">
            <label className="flex items-center gap-2 text-[13px] font-medium text-[#181818] cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={!!current.confirmation?.enabled}
                onChange={(e) => setConfirmation({ enabled: e.target.checked })}
                className="accent-[#0176D3] w-4 h-4"
                disabled={!emailFields.length}
              />
              Send the submitter a confirmation email
            </label>

            {!emailFields.length && (
              <p className="text-[11px] text-[#706E6B]">Add an Email field to the form first.</p>
            )}

            {current.confirmation?.enabled && emailFields.length > 0 && (
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Send To (Email Field)</label>
                  <select
                    className={inputClass}
                    value={current.confirmation?.emailFieldId || ''}
                    onChange={(e) => setConfirmation({ emailFieldId: e.target.value })}
                  >
                    <option value="">Select a field…</option>
                    {emailFields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Subject</label>
                  <input
                    className={inputClass}
                    placeholder="Thanks for submitting!"
                    value={current.confirmation?.subject || ''}
                    onChange={(e) => setConfirmation({ subject: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Message</label>
                  <textarea
                    className={inputClass}
                    rows={3}
                    placeholder="We've received your submission and will get back to you soon."
                    value={current.confirmation?.message || ''}
                    onChange={(e) => setConfirmation({ message: e.target.value })}
                  />
                </div>
                {!current.confirmation?.emailFieldId && (
                  <p className="text-[11px] text-[#EA001E]">Select an email field — without one, no confirmation can be sent.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
